import { ObservationStatusType } from '~/components/maud/patients/profile/clinical-profile/clinical-measurement/clinical-measurement-modal/constants';
import { ApiCommand } from '~/lib/Api';
import { GOAL_STATUS, ObservationGoals } from '~/lib/constants/goals.constants';
import { AdminEpisode, UserRole } from '~/lib/constants/patients.constants';
import urlConstants from '~/lib/constants/url.constants';
import type {
  CandidatePatient,
  CreateReassignmentRequest,
  GetCandidatePatientsResponse,
  GetReassignmentReasonsResponse,
  GetReassignmentsResponse,
  Reassignment,
  ReassignmentReason,
} from '~/lib/types/reassignment';
import { joinQueryParams } from '~/lib/util/api';

import api from './api';
import { CacheTag } from './CacheTag';
import { ContentTypes, HttpHeaders, HttpRequestMethod } from '..';

const {
  createReassignments: createReassignmentsUrl,
  getCandidatePatients: getCandidatePatientsUrl,
  getReassignmentReasons: getReassignmentReasonsUrl,
  getReassignments: getReassignmentsUrl,
  updateReassignment: updateReassignmentUrl,
  deleteReassignment: deleteReassignmentUrl,
} = urlConstants.reassignments;

const {
  getAdminEpisodesByPatientId: getAdminEpisodesByPatientIdUrl,
  getBillingPeriodById: getBillingPeriodByIdUrl,
  getDuplicateInquiryCoverages: getDuplicateInquiryCoveragesUrl,
  getEpisodeStatuses: getEpisodeStatusesUrl,
  getEquipPayorPayorPrograms: getEquipPayorPayorProgramsUrl,
  getEquipPayors: getEquipPayorsUrl,
  getPatientBillingPeriods: getPatientBillingPeriodsUrl,
  getPatientCoverages: getPatientCoveragesUrl,
  updatePatientBillingPeriod: updatePatientBillingPeriodUrl,
  deleteBillingPeriod: getDeleteBillingPeriodUrl,
  getPatientClinicalProfile: getPatientClinicalProfileUrl,
  getObservations: getPatientClinicalMeasurementsUrl,
  getPatientTreatmentEpisodes: getTreatmentEpisodesUrl,
  getProviderTeamAssignments: getProviderTeamAssignmentUrl,
  putPatientTreatmentEpisode: updateTreatmentEpisodeUrl,
  getWeightGoals: getPatientWeightGoalsUrl,
  getMedianBMI: getMedianBMIUrl,
  updateObservation,
  saveObservation,
  getObservationsHistory,
  saveGoal: saveObservationGoal,
  updateGoal: updateObservationGoal,
  deleteGoal: voidGoal,
  getActivitiesByPatientId,
  getDiagnosisCodesSearch: getDiagnosisCodesSearchUrl,
  getBodyTraceDevices: getBodyTraceDevicesUrl,
  getSupportDetailsBySupportExternalId,
  createPatientBillingPeriod: createPatientBillingPeriodUrl,
  updatePatientBillingPeriodV2: updatePatientBillingPeriodV2Url,
  getBodyTraceOrders: getBodyTraceOrdersUrl,
  addBodyTraceOrders: addBodyTraceOrdersUrl,
  addBodyTraceReorder: addBodyTraceReorderUrl,
  getBodyTraceScaleReorderReasons: getBodyTraceScaleReorderReasonsUrl,
} = urlConstants.clinical_data;

interface FilterOptionsType {
  label: string;
  externalId: string;
}

export type AddObservationRequest = {
  patientId: string;
  createdBy: string;
  interpretation?: string;
  note?: string;
  observationDatetime: string;
  position?: string;
  secondaryValue: number | null;
  source: 'Manual';
  status: ObservationStatusType;
  type: string;
  unit: string | string[];
  updatedByUserId: string;
  value: number;
};

export type SaveObservationGoalRequest = {
  patientId: string;
  goalEndDate?: string;
  goalAssigneeIds: string[];
  goalFrequency: string;
  goalFrequencyReason: string | null;
  goalMethod: string;
  goalStatus: GOAL_STATUS;
  goalType: string;
  highBound: number | null;
  lowBound: number | null;
  goalStartDate: string;
  typeName: ObservationGoals;
  unit: string;
};

type NotificationPreferences = {
  receiveEmails: boolean;
  receiveSms: boolean;
};
type Preferences = {
  notifications: NotificationPreferences;
};

interface Address {
  street: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  county: string;
}

interface Status {
  code: string;
  label: string;
}

export type ScaleReorderReason = {
  code: string;
  label: string;
};

interface Device {
  id: string;
  status: Status;
  type: string;
  dateActive: string;
}

export interface StatusHistoryEntry {
  status: Status;
  timestamp: string;
}

export interface GetBodyTraceOrderResponse {
  orderId: string;
  shipToAddressId: string;
  shipToAddress: Address;
  addressFirstName: string;
  addressLastName: string;
  addressEmail: string;
  addressPhoneNumber: string;
  linkedId: string | null;
  status: Status;
  patientExternalId: string;
  device: Device;
  bodyTraceOrderId: string;
  createdByDisplayFirstName?: string;
  createdByDisplayLastName?: string;
  createdByFirstName: string;
  createdByLastName: string;
  providerAvatarDetails?: AvatarDetails;
  providerExternalId?: string;
  trackingNumber: string | null;
  statusHistory: StatusHistoryEntry[];
  addressTypeLabel?: UserRole;
}

type SupportAddress = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
};
export type ReorderBodyTraceRequest = OrderBodyTraceRequest & {
  reorderReason: string;
};
export type GetSupportDetailsBySupportIdResponse = {
  supportExternalId: string;
  firstName: string;
  lastName: string;
  address: SupportAddress;
  email: string;
  cellPhone: string;
  contactPreferences: string[];
  gender: string;
  pronouns: string[];
  languages: string[];
  isDeleted: boolean;
  timezone: string;
  chosenName: string;
  prefix: string;
  middleName: string;
  suffix: string;
  dateOfBirth: string;
  ethnicities: string[];
  isAccountDeactivated: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  preferences: Preferences;
};

type DiagnosisCodesSearchRequest = {
  exactMatch?: boolean;
  isAdmissions?: boolean;
  isBillable?: boolean;
  pageNumber?: number;
  pageSize?: number;
  searchString?: string;
};

type GetPatientCoveragesRequest = {
  inquiryId?: string;
  patientId: string;
};

type UpdateTreatmentEpisodeRequestParams = {
  patientId: string;
  inquiryId?: string;
  treatmentEpisodeId: string;
  body: UpdateTreatmentEpisodeRequest;
};

type ShipToAddress = {
  street: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
  county?: string;
};
export type OrderBodyTraceRequest = {
  shipToAddressId?: string;
  patientExternalId: string;
  shipToAddress?: ShipToAddress;
  addressFirstName: string;
  addressLastName: string;
  addressEmail: string;
  addressPhoneNumber: string;
  addressTypeLabel?: string;
};
export type OrderBodyTraceResponse = {
  orderId: string;
};

export const clinicalDataApi = api.injectEndpoints({
  endpoints: (build) => ({
    createBillingPeriod: build.mutation<
      CreateBillingPeriodMutationResponse,
      Partial<BillingPeriod>
    >({
      invalidatesTags: (_, __, arg) => [
        {
          id: arg.patientExternalId,
          type: CacheTag.BillingPeriods,
        },
      ],
      query: (body) => ({
        body,
        method: ApiCommand.POST,
        url: createPatientBillingPeriodUrl(body.patientExternalId),
      }),
    }),
    addBodyTraceOrders: build.mutation<
      OrderBodyTraceResponse,
      OrderBodyTraceRequest
    >({
      invalidatesTags: [CacheTag.EquipScaleOrder],
      query: (body) => ({
        body,
        method: ApiCommand.POST,
        url: addBodyTraceOrdersUrl,
      }),
    }),
    addBodyTraceReorder: build.mutation<
      OrderBodyTraceResponse,
      ReorderBodyTraceRequest
    >({
      invalidatesTags: [CacheTag.EquipScaleOrder],
      query: (body) => ({
        body,
        method: ApiCommand.POST,
        url: addBodyTraceReorderUrl,
      }),
    }),
    deleteBillingPeriod: build.mutation<void, DeleteBillingPeriodRequest>({
      invalidatesTags: (_, __, arg) => [
        {
          id: arg.patientExternalId,
          type: CacheTag.BillingPeriods,
        },
      ],
      query: ({ patientExternalId, billingPeriodId, deleteReasonCode }) => ({
        body: `deleteReasonCode=${deleteReasonCode}`,
        method: HttpRequestMethod.DELETE,
        url: getDeleteBillingPeriodUrl(billingPeriodId, patientExternalId),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }),
    }),
    getAdminEpisodesByPatientId: build.query<AdminEpisode[], string>({
      query: (patientExternalId) =>
        getAdminEpisodesByPatientIdUrl(patientExternalId),
    }),
    getAdminEpisodeStatuses: build.query<FilterOptionsType[], void>({
      query: () => getEpisodeStatusesUrl,
    }),
    getBillingPeriodById: build.query<GetBillingPeriodByIdResponse, string>({
      query: (billingPeriodId) => getBillingPeriodByIdUrl(billingPeriodId),
    }),
    getBodyTraceDevices: build.query<BodyTraceDevices[], string>({
      query: (patientId) => getBodyTraceDevicesUrl(patientId),
    }),
    getBodyTraceOrders: build.query<GetBodyTraceOrderResponse[], string>({
      providesTags: [CacheTag.EquipScaleOrder],
      query: (patientExternalId) => getBodyTraceOrdersUrl(patientExternalId),
    }),
    getBodyTraceScaleReorderReasons: build.query<ScaleReorderReason[], void>({
      query: () => getBodyTraceScaleReorderReasonsUrl,
    }),
    getDuplicateInquiryCoverages: build.query<
      GetDuplicateCoveragesResponse,
      GetDuplicateCoveragesRequest
    >({
      query: ({ coverageDate, inquiryId, patientId }) =>
        getDuplicateInquiryCoveragesUrl(inquiryId, coverageDate, patientId),
    }),
    getEquipPayors: build.query<EquipPayor[], string>({
      query: (payorExternalId) => getEquipPayorsUrl(payorExternalId),
      transformResponse: (response: EquipPayor[]) => {
        return response.sort((a, b) => a.name.localeCompare(b.name));
      },
    }),
    getPatientBillingPeriods: build.query<GetBillingPeriodsResponse, string>({
      providesTags: (_, __, patientExternalId) => [
        { id: patientExternalId, type: CacheTag.BillingPeriods },
      ],
      query: (patientExternalId) =>
        getPatientBillingPeriodsUrl(patientExternalId),
    }),
    getPatientClinicalProfile: build.query<PatientClinicalProfile, string>({
      providesTags: (result, error, patientId) => [
        { id: patientId, type: CacheTag.PatientClinicalProfile },
      ],
      query: (patientExternalId) =>
        getPatientClinicalProfileUrl(patientExternalId),
    }),
    getPatientCoverages: build.query<
      PatientCoverage[],
      GetPatientCoveragesRequest
    >({
      providesTags: (result, error, request) => [
        { id: request.inquiryId, type: CacheTag.Inquiry },
        { id: request.patientId, type: CacheTag.PatientCoveragesById },
      ],
      query: ({ patientId, inquiryId }) => ({
        params: { 'inquiry-external-id': inquiryId },
        url: getPatientCoveragesUrl(patientId),
      }),
    }),
    getPatientTreatmentEpisodes: build.query<
      GetTreatmentEpisodesResponse,
      string
    >({
      providesTags: (_, __, patientId) => [
        { id: patientId, type: CacheTag.PatientTreatmentEpisodes },
      ],
      query: (patientId) => getTreatmentEpisodesUrl(patientId),
    }),
    getProviderTeamAssignments: build.query<
      GetProviderTeamAssignmentResponse,
      string
    >({
      providesTags: (_, __, patientId) => [
        { id: patientId, type: CacheTag.PatientProviderTeamAssignments },
      ],
      query: (patientId) => getProviderTeamAssignmentUrl(patientId),
    }),
    updatePatientTreatmentEpisode: build.mutation<
      void,
      UpdateTreatmentEpisodeRequestParams
    >({
      invalidatesTags: (_, __, arg) => [
        {
          id: arg.patientId,
          type: CacheTag.PatientTreatmentEpisodes,
        },
        ...(arg.inquiryId
          ? [
              {
                id: arg.inquiryId,
                type: CacheTag.Inquiry,
              },
            ]
          : []),
        {
          id: arg.patientId,
          type: CacheTag.GetPatientById,
        },
        {
          id: arg.patientId,
          type: CacheTag.PatientInquiriesSummary,
        },
      ],
      query: ({ body, patientId, treatmentEpisodeId }) => ({
        body,
        method: ApiCommand.PUT,
        url: updateTreatmentEpisodeUrl(patientId, treatmentEpisodeId),
      }),
    }),
    getPayorPrograms: build.query<PayorProgram[], string>({
      query: (equipPayorId) => getEquipPayorPayorProgramsUrl(equipPayorId),
      transformResponse: (response: PayorProgram[]) => {
        return response.sort((a, b) => a.label.localeCompare(b.label));
      },
    }),
    updateBillingPeriod: build.mutation<
      CreateBillingPeriodMutationResponse,
      Partial<BillingPeriod>
    >({
      invalidatesTags: (_, __, arg) => [
        {
          id: arg.patientExternalId,
          type: CacheTag.BillingPeriods,
        },
      ],
      query: (body) => ({
        body,
        method: ApiCommand.PUT,
        url: updatePatientBillingPeriodUrl(
          body.patientExternalId,
          body.billingPeriodId,
        ),
      }),
    }),
    updateBillingPeriodV2: build.mutation<
      CreateBillingPeriodMutationResponse,
      Partial<BillingPeriod>
    >({
      invalidatesTags: (_, __, arg) => [
        {
          id: arg.patientExternalId,
          type: CacheTag.BillingPeriods,
        },
      ],
      query: (body) => ({
        body,
        method: ApiCommand.PUT,
        url: updatePatientBillingPeriodV2Url(
          body.patientExternalId,
          body.billingPeriodId,
        ),
      }),
    }),
    getDiagnosisCodesSearch: build.query<
      DiagnosisCode[],
      DiagnosisCodesSearchRequest
    >({
      keepUnusedDataFor: 60 * 60,
      query: ({
        exactMatch,
        isAdmissions,
        isBillable,
        pageNumber,
        pageSize,
        searchString,
      }) => ({
        params: {
          'exact-match': exactMatch,
          'is-admissions': isAdmissions,
          'is-billable': isBillable,
          'page-number': pageNumber,
          'page-size': pageSize,
          'search-string': searchString,
        },
        url: getDiagnosisCodesSearchUrl,
      }),
    }),
    getPatientClinicalMeasurements: build.query<
      FetchPatientVitalsObservationsResponse,
      GetPatientClinicalMeasurementsQueryParams
    >({
      providesTags: [CacheTag.Observations],
      query: ({ patientExternalId, filterBy, pageNumber, pageSize }) => {
        const params = {};
        if (filterBy !== undefined) {
          params['filter-by'] = joinQueryParams(filterBy);
          params['page-number'] = pageNumber;
          params['page-size'] = pageSize;
        }
        return {
          params,
          url: getPatientClinicalMeasurementsUrl(patientExternalId),
        };
      },
    }),
    getPatientWeightGoals: build.query<
      PatientWeightGoalType[],
      { patientExternalId: string; observationType: string[] }
    >({
      providesTags: [CacheTag.Observations, CacheTag.EquipScaleOrder],
      query: ({ patientExternalId, observationType }) => {
        const params = {};
        if (observationType !== undefined) {
          params['observation-type'] = observationType;
        }
        return {
          params,
          url: getPatientWeightGoalsUrl(patientExternalId),
        };
      },
    }),
    getSupportDetailsBySupportExternalId: build.query<
      GetSupportDetailsBySupportIdResponse,
      string
    >({
      query: (supportExternalId) =>
        getSupportDetailsBySupportExternalId(supportExternalId),
    }),
    getMedianBMI: build.query<
      { medianBMI: number },
      { age: number; sex: string }
    >({
      query: ({ age, sex }) => {
        return {
          params: { age, sex },
          url: getMedianBMIUrl,
        };
      },
    }),
    updateObservation: build.mutation<string, PatientVitalsObservation>({
      invalidatesTags: [CacheTag.Observations],
      query: (body) => {
        return {
          body,
          method: ApiCommand.PUT,
          url: updateObservation(
            body.patientExternalId,
            body.observationExternalId,
          ),
          responseHandler: (response) => response.text(),
        };
      },
    }),
    addObservation: build.mutation<string, AddObservationRequest>({
      invalidatesTags: [CacheTag.Observations],
      query: (body) => ({
        body,
        method: ApiCommand.POST,
        url: saveObservation(body.patientId),
        responseHandler: (response) => response.text(),
      }),
    }),
    getObservationHistory: build.query<
      PatientVitalsObservationHistory[],
      string
    >({
      providesTags: [CacheTag.Observations],
      query: (observationExternalId) => {
        return {
          url: getObservationsHistory(observationExternalId),
        };
      },
    }),
    saveObservationGoal: build.mutation<string, SaveObservationGoalRequest>({
      invalidatesTags: [CacheTag.Observations, CacheTag.EquipScaleOrder],
      query: (body) => {
        return {
          body,
          method: ApiCommand.POST,
          url: saveObservationGoal(body.patientId),
          responseHandler: (response) => response.text(),
        };
      },
    }),
    updateObservationGoal: build.mutation<
      string,
      SaveObservationGoalRequest & { observationGoalExternalId: string }
    >({
      invalidatesTags: [CacheTag.Observations, CacheTag.EquipScaleOrder],
      query: (body) => {
        return {
          body,
          method: ApiCommand.PUT,
          url: updateObservationGoal(
            body.patientId,
            body.observationGoalExternalId,
          ),
          responseHandler: (response) => response.text(),
        };
      },
    }),
    voidGoal: build.mutation<
      string,
      { patientId: string; observationGoalExternalId: string }
    >({
      invalidatesTags: [CacheTag.Observations, CacheTag.EquipScaleOrder],
      query: ({ patientId, observationGoalExternalId }) => {
        return {
          method: ApiCommand.DELETE,
          url: voidGoal(patientId, observationGoalExternalId),
        };
      },
    }),
    getGoalHistory: build.query<
      PatientActivityListObject[],
      {
        patientId: string;
        filterBy: { 'external-id': string; 'filter-type': string };
      }
    >({
      providesTags: [CacheTag.Observations],
      query: ({ patientId, filterBy }) => {
        const params = {};
        if (filterBy) {
          params['filter-by'] = joinQueryParams(filterBy);
        }
        return {
          params,
          url: getActivitiesByPatientId(patientId),
        };
      },
    }),
    createReassignments: build.mutation<void, CreateReassignmentRequest>({
      invalidatesTags: (_, __, { departingProviderId, patientExternalIds }) => [
        { id: departingProviderId, type: CacheTag.CandidatePatients as const },
        ...patientExternalIds.map((id) => ({
          id,
          type: CacheTag.PatientReassignments as const,
        })),
      ],
      query: (body) => ({
        body,
        method: ApiCommand.POST,
        url: createReassignmentsUrl,
      }),
    }),
    getCandidatePatients: build.query<CandidatePatient[], string>({
      providesTags: (_, __, providerId) => [
        { id: providerId, type: CacheTag.CandidatePatients },
      ],
      query: (providerId) => getCandidatePatientsUrl(providerId),
      transformResponse: (response: GetCandidatePatientsResponse) =>
        response.data.map(({ patient, reassignment }) => ({
          patientExternalId: patient.id,
          chosenName: patient.chosenName,
          firstName: patient.firstName,
          lastName: patient.lastName,
          dateOfBirth: patient.dateOfBirth,
          hasPendingReassignment: reassignment !== null,
          failureReason: reassignment?.failureReason ?? null,
        })),
    }),
    getReassignmentReasons: build.query<ReassignmentReason[], void>({
      query: () => getReassignmentReasonsUrl,
      transformResponse: (response: GetReassignmentReasonsResponse) =>
        response.reassignmentReasons,
    }),
    getReassignments: build.query<GetReassignmentsResponse, string>({
      providesTags: (_, __, patientId) => [
        { id: patientId, type: CacheTag.PatientReassignments },
      ],
      query: (patientId) => getReassignmentsUrl(patientId),
      transformResponse: (response: GetReassignmentsResponse | { data: GetReassignmentsResponse }) =>
        Array.isArray(response) ? response : response.data,
    }),
    updateReassignmentDate: build.mutation<
      Reassignment,
      { reassignmentId: string; reassignmentDate: string }
    >({
      invalidatesTags: [CacheTag.PatientReassignments],
      query: ({ reassignmentId, reassignmentDate }) => ({
        body: { reassignmentDate },
        method: ApiCommand.PUT,
        url: updateReassignmentUrl(reassignmentId),
      }),
    }),
    voidReassignment: build.mutation<void, string>({
      invalidatesTags: [
        CacheTag.CandidatePatients,
        CacheTag.PatientReassignments,
        CacheTag.PatientProviderTeamAssignments,
      ],
      query: (reassignmentId) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.DELETE,
        url: deleteReassignmentUrl(reassignmentId),
      }),
    }),
  }),
});

export const {
  useCreateBillingPeriodMutation,
  useGetAdminEpisodesByPatientIdQuery,
  useGetBodyTraceOrdersQuery,
  useAddBodyTraceReorderMutation,
  useGetBodyTraceScaleReorderReasonsQuery,
  useGetEquipPayorsQuery,
  useGetPatientBillingPeriodsQuery,
  useGetPatientClinicalProfileQuery,
  useGetPatientCoveragesQuery,
  useLazyGetDuplicateInquiryCoveragesQuery,
  useGetPatientTreatmentEpisodesQuery,
  useGetProviderTeamAssignmentsQuery,
  useLazyGetProviderTeamAssignmentsQuery,
  useGetPayorProgramsQuery,
  useLazyGetBillingPeriodByIdQuery,
  useDeleteBillingPeriodMutation,
  useGetPatientClinicalMeasurementsQuery,
  useGetPatientWeightGoalsQuery,
  useGetMedianBMIQuery,
  useUpdateObservationMutation,
  useAddObservationMutation,
  useLazyGetObservationHistoryQuery,
  useSaveObservationGoalMutation,
  useUpdateObservationGoalMutation,
  useVoidGoalMutation,
  useGetGoalHistoryQuery,
  useGetDiagnosisCodesSearchQuery,
  useLazyGetDiagnosisCodesSearchQuery,
  useUpdatePatientTreatmentEpisodeMutation,
  useAddBodyTraceOrdersMutation,
  useGetSupportDetailsBySupportExternalIdQuery,
  useUpdateBillingPeriodV2Mutation,
  useCreateReassignmentsMutation,
  useGetCandidatePatientsQuery,
  useGetReassignmentReasonsQuery,
  useGetReassignmentsQuery,
  useUpdateReassignmentDateMutation,
  useVoidReassignmentMutation,
} = clinicalDataApi;
